import { useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiMutate, useApi } from "../hooks/useApi";
import { initials } from "../lib/builder-helpers";
import { istEmpfaengerDauerhaftBlockiert, mailfehlerText } from "../lib/mailfehler-anzeige";
import { openPrintableDocumentPost } from "../lib/print";
import { vorgangPfad } from "../lib/pfade";
import { Icon, Input, LoadingRows, Modal, Button, Checkbox, Textarea, DateInput } from "./components";
import { datumDe, gueltigBisProblem, heuteBerlin, VERANSTALTUNG_VERGANGEN_SATZ, veranstaltungVergangen } from "../lib/gueltigkeit";
import { FehlerOderHinweis } from "./FassungNeuSendenHinweis";
import { angebotSendenPfad } from "../lib/pfade";
import { anlassAnzeige, formatDate } from "./format";
import { Row } from "./Row";
import { UnzustellbarDialog } from "./UnzustellbarDialog";
import { alsUnzustellbar, type UnzustellbarInfo } from "../lib/unzustellbar";

/**
 * "Erinnerung senden" (09.09.2026, Antoine-Screenshot der Veranstaltungsseite): der rote Knopf
 * schickte bisher SOFORT, ohne dass der Betreiber sah, was rausgeht ("Ich will dann wieder das
 * Mailformular in der Mitte sehen und alles auswählen können"). Dieser Dialog ist bewusst KEIN
 * Klon von `AngebotSendenDialog.tsx` (der ist auf den Erstversand zugeschnitten: Varianten-
 * Auswahl/-Abwahl, KI-Anschreiben-Neugenerierung, Kostenübernahme-Anforderung — nichts davon
 * gilt fuer eine Erinnerung, die per Definition dieselbe bereits versendete Version unveraendert
 * wiederholt, s. Kommentar an `POST /offers/resend`). Uebernommen ist das INTERAKTIONS-MUSTER
 * desselben Dialogs 1:1: Empfaenger-Zeile, Betreff-Feld, Nachricht-Textarea, Vorschau-Knopf,
 * Abbrechen/Jetzt-senden-Footer, derselbe Erfolg/Fehlschlag-Bildschirm danach.
 *
 * Datengrundlage: `GET .../offers/resend-preview` liefert beim Oeffnen die ECHTEN Vorschlags-
 * werte (Betreff + Anschreiben aus dem eingefrorenen Versand-Snapshot, nicht KI-generiert) -
 * das ist bereits exakt das, was ein Klick auf "Jetzt senden" ohne jede Aenderung verschicken
 * wuerde. Editiert der Betreiber, sendet `POST .../offers/resend` diese editierten Werte zurueck.
 */

interface ResendPreviewData {
  subject: string;
  letterText: string;
  toAddr: string | null;
  customerName: string | null;
  companyEmail: string | null;
  occasion: string | null;
  eventDate: string | null;
  guestCount: number | null;
  version: number;
  /** Abgelaufene Gültigkeit (Antoine, 04.11.2026): der Dialog bietet an, sie zu verlängern, statt eine Erinnerung ohne Annahmemöglichkeit zu senden. */
  gueltigBis?: string | null;
  gueltigkeitAbgelaufen?: boolean;
  vorschlagGueltigBis?: string;
  veranstaltungAm?: string | null;
}

interface ResendResult {
  version: number;
  emailSent: boolean;
  emailError: string | null;
  subject: string;
}

export function ErinnerungSendenDialog({
  open,
  onClose,
  eventId,
  eventNumber,
  onSent,
  onTerminAendern,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  eventNumber: string | null | undefined;
  /** Nach erfolgreichem Versand: eventQ/offersQ/activityQ neu laden (Elternseite, wie bisher
   *  in `sendReminder()`). */
  onSent: () => void;
  /** Ausweg bei vergangenem Veranstaltungstag: führt zur Termin-Bearbeitung. */
  onTerminAendern?: () => void;
}) {
  const previewQ = useApi<ResendPreviewData>(open && eventId ? `/api/events/${eventId}/offers/resend-preview` : null);

  const [phase, setPhase] = useState<"compose" | "sent" | "mail_failed">("compose");
  const [subject, setSubject] = useState("");
  const [letterText, setLetterText] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [mailError, setMailError] = useState<string | null>(null);
  const [result, setResult] = useState<ResendResult | null>(null);
  // Neuversand-Schutz (20.10.2026): 409 `adresse_unzustellbar` -> Dialog mit neuer Adresse.
  const [unzustellbar, setUnzustellbar] = useState<UnzustellbarInfo | null>(null);
  // Neues „Gültig bis“, wenn das Angebot abgelaufen ist (Vorgabe +14 Tage, nie über den Veranstaltungstag hinaus).
  const [neuBis, setNeuBis] = useState("");

  // Wie `AngebotSendenDialog`: bei jedem Oeffnen synchron zuruecksetzen (useLayoutEffect, kein
  // Frame lang der alte sent/mail_failed-Bildschirm sichtbar).
  useLayoutEffect(() => {
    if (open) {
      setPhase("compose");
      setSendError(null);
      setMailError(null);
      setResult(null);
    }
  }, [open]);

  // Sobald die echten Vorschlagswerte da sind, Felder damit befuellen - nur beim ERSTEN Laden
  // nach dem Oeffnen (nicht bei jedem `previewQ.reload()`), sonst ueberschreibt ein spaeterer
  // Refetch eine bereits laufende Bearbeitung.
  const [prefilledFor, setPrefilledFor] = useState<string | null>(null);
  const prefillKey = eventId && previewQ.data ? `${eventId}:${previewQ.data.version}` : null;
  if (open && prefillKey && prefillKey !== prefilledFor) {
    setPrefilledFor(prefillKey);
    setSubject(previewQ.data!.subject);
    setLetterText(previewQ.data!.letterText);
    setNeuBis(previewQ.data!.vorschlagGueltigBis ?? "");
  }
  if (!open && prefilledFor !== null) setPrefilledFor(null);

  // `useApi` reicht bei Fehlern nur `.message` durch (kein `statusCode`/`code`, s. `hooks/useApi.ts`
  // `ApiEnvelope`-Kommentar) - darum auf den Wortlaut pruefen, den die Route selbst festlegt
  // ("erneut zustellbare" fuer 409, "not_found" ohne eigenes `message`-Feld fuer 404).
  const abgelaufen = previewQ.data?.gueltigkeitAbgelaufen === true;
  const vergangen = abgelaufen && veranstaltungVergangen(previewQ.data?.veranstaltungAm);
  const bisProblem = abgelaufen ? (vergangen ? VERANSTALTUNG_VERGANGEN_SATZ : gueltigBisProblem(neuBis, previewQ.data?.veranstaltungAm, heuteBerlin(), previewQ.data?.gueltigBis)) : null;
  const notResendable = previewQ.error != null && (/erneut zustellbare/i.test(previewQ.error) || previewQ.error === "not_found");

  async function handlePreview() {
    if (!eventId) return;
    try {
      await openPrintableDocumentPost(`/api/events/${eventId}/offers/resend-preview`, { subject, letterText, ...(abgelaufen && !bisProblem ? { gueltigBisNeu: neuBis } : {}) });
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Der Versand selbst; wirft bei Fehlern (auch bei 409 `adresse_unzustellbar`). */
  async function sendeRequest(optionen: { trotzdem?: boolean }) {
    if (!eventId) return;
    const r = await apiMutate<ResendResult>(`/api/events/${eventId}/offers/resend`, "POST", {
      subject,
      letterText,
      ccSelf,
      ...(abgelaufen ? { gueltigBisNeu: neuBis } : {}),
      ...(optionen.trotzdem ? { trotzdem: true } : {}),
    });
    setResult(r);
    if (r.emailSent) {
      setPhase("sent");
      onSent();
    } else {
      setMailError(r.emailError);
      setPhase("mail_failed");
    }
  }

  async function handleSend() {
    if (!eventId) return;
    setSending(true);
    setSendError(null);
    try {
      await sendeRequest({});
    } catch (err) {
      const u = alsUnzustellbar(err);
      if (u) setUnzustellbar(u);
      else setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  const empfaengerBlockiert = istEmpfaengerDauerhaftBlockiert(mailError);

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      width={phase === "compose" ? "560px" : "468px"}
      title={
        previewQ.loading || previewQ.error
          ? "Erinnerung senden"
          : notResendable
            ? "Keine Erinnerung möglich"
            : phase === "compose"
              ? `Erinnerung an ${previewQ.data?.customerName ?? "Kunde"} senden`
              : phase === "mail_failed"
                ? "Erinnerung NICHT versendet"
                : "Erinnerung gesendet"
      }
      subtitle={
        !previewQ.loading && !previewQ.error && !notResendable && phase === "compose"
          ? [anlassAnzeige(previewQ.data?.occasion), previewQ.data?.eventDate ? formatDate(previewQ.data.eventDate) : null, previewQ.data?.guestCount ? `${previewQ.data.guestCount} Gäste` : null, eventNumber ? `Angebot ${eventNumber}` : null]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      footer={
        previewQ.loading ? undefined : notResendable || previewQ.error ? (
          <Button type="button" onClick={onClose}>
            Schließen
          </Button>
        ) : phase === "compose" ? (
          // Schmal (Mobil): Hauptknopf volle Breite oben, darunter Vorschau/Abbrechen nebeneinander; ab sm wie bisher eine Reihe.
          <div className="flex w-full flex-col-reverse gap-2.5 sm:flex-row sm:items-center sm:justify-end" data-testid="erinnerung-footer">
            <div className="flex gap-2.5 [&>*]:flex-1 sm:contents sm:[&>*]:flex-none">
              <Button type="button" variant="secondary" className="sm:mr-auto" onClick={() => void handlePreview()}>
                <Icon name="visibility" className="text-[16px]" /> Vorschau
              </Button>
              <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
                Abbrechen
              </Button>
            </div>
            <Button type="button" className="w-full sm:w-auto" data-testid="erinnerung-jetzt-senden" onClick={() => void handleSend()} disabled={sending || !subject.trim() || (abgelaufen && bisProblem !== null)}>
              <Icon name="send" className="text-[16px]" />{" "}
              {sending ? "Sendet …" : abgelaufen && neuBis && !bisProblem ? "Verlängern und senden" : "Jetzt senden"}
            </Button>
          </div>
        ) : phase === "mail_failed" ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Schließen
            </Button>
            {!empfaengerBlockiert && (
              <Button type="button" onClick={() => void handleSend()} disabled={sending}>
                <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "E-Mail erneut senden"}
              </Button>
            )}
          </>
        ) : (
          result && (
            <>
              {eventId && (
                <Link to={vorgangPfad(eventId)} className="mr-auto text-[12.5px] font-semibold text-secondary-fixed-dim hover:underline" onClick={onClose}>
                  Zum Vorgang
                </Link>
              )}
              <Button type="button" onClick={onClose}>
                Fertig
              </Button>
            </>
          )
        )
      }
    >
      {previewQ.loading ? (
        <LoadingRows rows={3} />
      ) : notResendable ? (
        <p className="text-[12.5px] text-secondary-fixed-dim">
          Der Vorgang hat gerade keine erneut zustellbare Angebotsversion (Status/Version geändert?).
        </p>
      ) : previewQ.error ? (
        <FehlerOderHinweis message={previewQ.error} zumSenden={eventId ? angebotSendenPfad(eventId) : undefined} />
      ) : phase === "compose" ? (
          <div className="space-y-3.5">
            {/* Empfänger */}
            <div>
              <label className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Empfänger</label>
              <div className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/40">
                <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[11px] font-semibold text-primary">
                  {initials(previewQ.data?.customerName)}
                </span>
                <span className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{previewQ.data?.customerName ?? "Kunde"}</span>
                <span className="truncate text-[12px] text-secondary-fixed-dim">{previewQ.data?.toAddr ?? "keine E-Mail hinterlegt"}</span>
              </div>
              {previewQ.data?.companyEmail && (
                <label className="mt-1.5 flex items-center gap-2 text-[12px] text-on-surface-variant">
                  <Checkbox checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} />
                  Kopie an mich senden ({previewQ.data.companyEmail})
                </label>
              )}
            </div>

            {abgelaufen && previewQ.data?.gueltigBis ? (
              <div className="space-y-2 rounded-lg border border-outline-variant/60 px-3 py-3 dark:border-white/10" data-testid="erinnerung-abgelaufen">
                <p className="text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  Das Angebot ist seit {datumDe(previewQ.data.gueltigBis)} abgelaufen.
                </p>
                {vergangen ? (
                  <>
                    <p className="text-[12px] text-secondary-fixed-dim" data-testid="erinnerung-vergangen-hinweis">{VERANSTALTUNG_VERGANGEN_SATZ}</p>
                    {onTerminAendern ? (
                      <Button type="button" variant="secondary" className="!py-2" data-testid="erinnerung-termin-aendern" onClick={() => { onClose(); onTerminAendern(); }}>
                        <Icon name="edit_calendar" className="text-[16px]" /> Termin ändern
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                <p className="text-[12px] text-secondary-fixed-dim">
                  Der Gast könnte nach dieser Erinnerung nicht mehr annehmen. Die Gültigkeit wird mit dem Senden verlängert; die Mail nennt das neue Datum.
                </p>
                {neuBis && !bisProblem ? <p className="text-[12px] font-semibold text-on-background dark:text-inverse-on-surface" data-testid="erinnerung-verlaengern-text">Gültigkeit bis {datumDe(neuBis)} verlängern und Erinnerung senden.</p> : null}
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Neu gültig bis</span>
                  <DateInput data-testid="erinnerung-gueltig-bis" value={neuBis} onValueChange={setNeuBis} min={heuteBerlin()} max={previewQ.data.veranstaltungAm ?? undefined} />
                </label>
                {bisProblem && neuBis ? <p className="text-[12px] text-error dark:text-[#e39b92]" data-testid="erinnerung-gueltig-problem">{bisProblem}</p> : null}
                  </>
                )}
              </div>
            ) : null}

            {/* Betreff */}
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
              <Input
                data-testid="erinnerung-betreff"
                className="!py-2.5"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={300}
              />
            </label>

            {/* Nachricht */}
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
              <Textarea
                data-testid="erinnerung-nachricht"
                className="min-h-[160px] text-[12.5px] leading-relaxed"
                value={letterText}
                onChange={(e) => setLetterText(e.target.value)}
                maxLength={4000}
                placeholder="Sehr geehrte Damen und Herren, …"
              />
              <p className="mt-1 text-[10.5px] text-secondary-fixed-dim">
                Dieselbe Angebotsversion wie beim letzten Versand geht erneut raus — nur Betreff und Anschreiben lassen sich hier anpassen.
              </p>
            </label>

            {sendError && <FehlerOderHinweis message={sendError} zumSenden={eventId ? angebotSendenPfad(eventId) : undefined} />}
          </div>
      ) : phase === "mail_failed" ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            {previewQ.data?.customerName ?? "Die Kundin"} hat <strong>keine E-Mail</strong> erhalten.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">
            {mailfehlerText(mailError)}
          </p>
        </div>
      ) : (
        result && (
          <div className="space-y-[9px]">
            <p className="pb-2 text-[12.5px] text-secondary-fixed-dim">
              {previewQ.data?.customerName ?? "Die Kundin"} wurde per E-Mail erinnert.
            </p>
            <Row label="Empfänger" value={previewQ.data?.toAddr ?? "–"} />
            <Row label="Betreff" value={result.subject} />
            <Row label="Version" value={String(result.version)} />
          </div>
        )
      )}
    </Modal>
    <UnzustellbarDialog
      info={unzustellbar}
      onClose={() => setUnzustellbar(null)}
      onSenden={async (optionen) => {
        await sendeRequest(optionen);
        setUnzustellbar(null);
      }}
    />
    </>
  );
}
